import { BlogConfig, BlogPostPreviewItem, BlogProcessor } from ".";
import type { BlogStore } from "../blogStore";
import { NotFoundError } from "../../util/fetch";

type SnowProfileType = "monitoring" | "accident";
type SnowProfileStability = "good" | "fair" | "poor" | "none";
type SnowProfileStatus = "draft" | "published";

export interface GromScore {
  grainTypeCf: "Persistent" | "Non-Persistent" | "Cap CF";
  hardnessDiffCfSurface: ">= 1" | "< 1";
  cfDepth: "<= 1m" | "> 1m";
  ectResult: "ECTP" | "ECTN" | "ECTX";
  absoluteNum: number;
  structureScore: number;
  strengthScore: number;
  propagationScore: number;
  gromScore: number;
}

interface SnowProfileApi {
  id: string;
  date: string;
  place: {
    lat: number | null;
    lng: number | null;
    name: string;
  };
  type: SnowProfileType;
  stability: SnowProfileStability;
  orientation: string | null;
  height: number | null;
  status: SnowProfileStatus;
  imageGallery: string[];
  content: string | null;
  creationDate: string;
  grom: GromScore | null;
}

const PAGE_SIZE = 200;

export class SnowProfilesProcessor implements BlogProcessor {
  async loadBlogPosts(
    config: BlogConfig,
    state?: BlogStore
  ): Promise<BlogPostPreviewItem[]> {
    const profiles = await this.fetchAllProfiles(state);
    const filtered = this.filterProfilesBySearch(profiles, state?.searchText);
    return filtered.map(profile => this.newItem(profile, config));
  }

  async loadBlogPost(
    config: BlogConfig,
    postId: unknown
  ): Promise<BlogPostPreviewItem> {
    const params = new URLSearchParams({ imageMode: "presigned" });
    const url = `${window.config.apis.snowProfiles}/${encodeURIComponent(String(postId))}?${params.toString()}`;
    const profile = await this.fetchProfile(url);
    return this.newItem(profile, config);
  }

  private async fetchAllProfiles(state?: BlogStore): Promise<SnowProfileApi[]> {
    const all: SnowProfileApi[] = [];
    let offset = 0;
    let hasMore = true;

    while (hasMore) {
      const params = this.newParams(state, offset);
      const url = `${window.config.apis.snowProfiles}?${params.toString()}`;
      const response = await this.fetchProfilesPage(url);
      all.push(...response.items);

      hasMore =
        response.hasMore ??
        (response.items.length === PAGE_SIZE ||
          response.totalCount > all.length);
      if (response.items.length === 0) {
        hasMore = false;
      }
      offset += response.items.length;
    }

    return all;
  }

  private newParams(state: BlogStore | undefined, offset: number) {
    const params = new URLSearchParams({
      imageMode: "presigned",
      limit: String(PAGE_SIZE),
      offset: String(offset)
    });

    const startDate = this.instantToDateString(state?.startDate);
    if (startDate) {
      params.set("startDate", startDate);
    }

    const endDate = this.instantToDateString(state?.endDate, true);
    if (endDate) {
      params.set("endDate", endDate);
    }

    return params;
  }

  private instantToDateString(
    instant?: Temporal.Instant,
    subtractOneDay = false
  ): string | undefined {
    if (!instant) {
      return undefined;
    }
    const zone = Temporal.Now.timeZoneId();
    const zdt = instant.toZonedDateTimeISO(zone);
    return (subtractOneDay ? zdt.subtract({ days: 1 }) : zdt)
      .toPlainDate()
      .toString();
  }

  private filterProfilesBySearch(
    profiles: SnowProfileApi[],
    searchText?: string
  ): SnowProfileApi[] {
    const search = searchText?.trim().toLowerCase();
    if (!search) {
      return profiles;
    }

    return profiles.filter(profile => {
      const haystack = [
        profile.id,
        profile.date,
        profile.place?.name,
        profile.place?.lat ?? "",
        profile.place?.lng ?? "",
        profile.type,
        profile.stability,
        profile.orientation ?? "",
        profile.height ?? "",
        profile.status,
        profile.creationDate,
        profile.content ?? ""
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(search);
    });
  }

  private async fetchProfile(url: string): Promise<SnowProfileApi> {
    const res = await fetch(url, {
      headers: this.headers
    });
    if (res.status === 404) {
      throw new NotFoundError();
    }
    if (!res.ok) {
      throw new Error(res.statusText);
    }
    return res.json();
  }

  private async fetchProfilesPage(url: string): Promise<{
    items: SnowProfileApi[];
    hasMore: boolean | undefined;
    totalCount: number;
  }> {
    const res = await fetch(url, {
      headers: this.headers
    });
    if (!res.ok) {
      throw new Error(res.statusText);
    }

    const items = (await res.json()) as SnowProfileApi[];
    const hasMoreHeader = res.headers.get("X-Has-More");
    const totalCountHeader = res.headers.get("X-Total-Count");

    return {
      items,
      hasMore:
        hasMoreHeader === null
          ? undefined
          : hasMoreHeader.toLowerCase() === "true",
      totalCount: totalCountHeader ? Number(totalCountHeader) : 0
    };
  }

  private get headers(): HeadersInit {
    const token = window.config.apiKeys.snowProfilesBearer;
    return {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  }

  private newItem(profile: SnowProfileApi, config: BlogConfig) {
    const language =
      config.lang ||
      document.body.parentElement?.lang?.slice(0, 2) ||
      window.config.techBlog.lang ||
      "en";
    const tags = this.metadataTags(profile);
    const gallery = Array.isArray(profile.imageGallery)
      ? profile.imageGallery
      : [];
    return new BlogPostPreviewItem(
      config.name,
      profile.id,
      `/blog/${config.name}/${profile.id}`,
      "",
      new Date(`${profile.date}T00:00:00Z`),
      profile.place?.name || profile.id,
      this.toHtmlContent(profile),
      language,
      [],
      config.regions,
      gallery[0] ?? null,
      tags,
      {
        id: profile.id,
        date: profile.date,
        placeName: profile.place?.name ?? "",
        placeLat: profile.place?.lat ?? null,
        placeLng: profile.place?.lng ?? null,
        type: profile.type,
        stability: profile.stability,
        orientation: profile.orientation,
        height: profile.height,
        status: profile.status,
        creationDate: profile.creationDate,
        imageGallery: gallery,
        grom: profile.grom ?? null
      }
    );
  }

  private metadataTags(profile: SnowProfileApi): string[] {
    const tags: string[] = [
      `Type: ${profile.type}`,
      `Stability: ${profile.stability}`
    ];
    if (profile.orientation) {
      const o = profile.orientation;
      tags.push(`Orientation: ${/^\d+$/.test(o) ? `${o}°` : o}`);
    }
    if (typeof profile.height === "number") {
      tags.push(`Height: ${profile.height} m`);
    }
    if (profile.grom) {
      tags.push(`GROM: ${profile.grom.gromScore}`);
    }
    return tags;
  }

  private toHtmlContent(profile: SnowProfileApi): string {
    const contentHtml = this.normalizeContent(profile.content);
    const galleryHtml = this.galleryToHtml(profile.imageGallery);
    return [contentHtml, galleryHtml].filter(Boolean).join("\n");
  }

  private normalizeContent(content: string | null): string {
    if (!content?.trim()) {
      return "";
    }
    // Keep rich text from backend; otherwise render plain text with line breaks.
    if (/<[a-z][\s\S]*>/i.test(content)) {
      return content;
    }
    return content
      .split(/\n{2,}/)
      .map(
        paragraph =>
          `<p>${this.escapeHtml(paragraph).replace(/\n/g, "<br />")}</p>`
      )
      .join("");
  }

  private galleryToHtml(images: string[] | null | undefined): string {
    if (!Array.isArray(images) || images.length === 0) {
      return "";
    }
    const items = images
      .map((src, index) => {
        const alt = `Snow profile image ${index + 1}`;
        return `<figure class="wp-block-image"><a href="${src}"><img src="${src}" alt="${alt}" title="${alt}" loading="lazy" /></a></figure>`;
      })
      .join("");
    return `<section class="snow-profile-gallery">${items}</section>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }
}
